import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  IconButton,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useForm, useFieldArray } from 'react-hook-form'
import { BankProfile, BankContact, BankProfileType } from '@otw/models'

interface BankProfileDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: Omit<BankProfile, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>) => void
  existingProfile?: BankProfile | null
  isSubmitting?: boolean
}

interface BankProfileFormData {
  profileType: BankProfileType
  bankName: string
  nickname: string
  invoiceInstructions: string
  accountNumber: string
  routingNumber: string
  memo: string
  email: string
  physicalAddress: {
    street: string
    city: string
    state: string
    zipCode: string
    country: string
  }
  overnightAddress: {
    street: string
    city: string
    state: string
    zipCode: string
    country: string
  }
  bankContacts: BankContact[]
}

export const BankProfileDialog: React.FC<BankProfileDialogProps> = ({
  open,
  onClose,
  onSubmit,
  existingProfile,
  isSubmitting = false,
}) => {
  const [error, setError] = useState<string | null>(null)
  const isEditMode = !!existingProfile

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BankProfileFormData>({
    defaultValues: {
      profileType: 'inventory',
      bankName: '',
      nickname: '',
      invoiceInstructions: '',
      accountNumber: '',
      routingNumber: '',
      memo: '',
      email: '',
      physicalAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US',
      },
      overnightAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US',
      },
      bankContacts: [{ name: '', phone: '', email: '' }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bankContacts',
  })

  useEffect(() => {
    if (open && existingProfile) {
      reset({
        profileType: existingProfile.profileType || 'sales',
        bankName: existingProfile.bankName || '',
        nickname: existingProfile.nickname || '',
        invoiceInstructions: existingProfile.invoiceInstructions || '',
        accountNumber: existingProfile.accountNumber || '',
        routingNumber: existingProfile.routingNumber || '',
        memo: existingProfile.memo || '',
        email: existingProfile.email || '',
        physicalAddress: {
          street: existingProfile.physicalAddress?.street || '',
          city: existingProfile.physicalAddress?.city || '',
          state: existingProfile.physicalAddress?.state || '',
          zipCode: existingProfile.physicalAddress?.zipCode || '',
          country: existingProfile.physicalAddress?.country || 'US',
        },
        overnightAddress: {
          street: existingProfile.overnightAddress?.street || '',
          city: existingProfile.overnightAddress?.city || '',
          state: existingProfile.overnightAddress?.state || '',
          zipCode: existingProfile.overnightAddress?.zipCode || '',
          country: existingProfile.overnightAddress?.country || 'US',
        },
        bankContacts:
          existingProfile.bankContacts && existingProfile.bankContacts.length > 0
            ? existingProfile.bankContacts
            : [{ name: '', phone: '', email: '' }],
      })
    } else if (open && !existingProfile) {
      reset({
        profileType: 'inventory',
        bankName: '',
        nickname: '',
        invoiceInstructions: '',
        accountNumber: '',
        routingNumber: '',
        memo: '',
        email: '',
        physicalAddress: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'US',
        },
        overnightAddress: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'US',
        },
        bankContacts: [{ name: '', phone: '', email: '' }],
      })
    }
  }, [open, existingProfile, reset])

  const profileType = watch('profileType')

  const onFormSubmit = (data: BankProfileFormData) => {
    try {
      setError(null)
      if (data.profileType === 'inventory') {
        onSubmit({
          profileType: 'inventory',
          bankName: data.bankName,
          nickname: data.nickname,
          accountNumber: data.accountNumber,
          routingNumber: data.routingNumber,
          memo: data.memo,
          email: data.email,
          physicalAddress: data.physicalAddress,
          invoiceInstructions: '',
          bankContacts: [],
          overnightAddress: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'US',
          },
        })
      } else {
        onSubmit({
          profileType: 'sales',
          bankName: data.bankName,
          nickname: data.nickname,
          accountNumber: data.accountNumber,
          routingNumber: data.routingNumber,
          invoiceInstructions: data.invoiceInstructions,
          overnightAddress: data.overnightAddress,
          bankContacts: data.bankContacts,
          memo: '',
          email: '',
          physicalAddress: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'US',
          },
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null)
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Bank Profile' : 'Create Bank Profile'}</DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Bank Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Name"
                {...register('bankName', { required: 'Bank name is required' })}
                error={!!errors.bankName}
                helperText={errors.bankName?.message}
                required
              />
            </Grid>

            {/* Nickname */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nickname"
                {...register('nickname')}
                placeholder="Optional short name for this bank profile"
                helperText="Optional nickname to help find this profile when searching"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Profile Type
              </Typography>
              <ToggleButtonGroup
                exclusive
                color="primary"
                value={profileType}
                onChange={(_, value) => {
                  if (value) {
                    setValue('profileType', value)
                  }
                }}
                size="small"
              >
                <ToggleButton value="inventory">Inventory</ToggleButton>
                <ToggleButton value="sales">Sales</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Account Number and Routing Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                {...register('accountNumber')}
                placeholder="Enter bank account number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Routing Number"
                {...register('routingNumber')}
                placeholder="Enter bank routing number"
              />
            </Grid>

            {profileType === 'inventory' ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Memo"
                    multiline
                    rows={3}
                    {...register('memo')}
                    placeholder="Optional memo or notes for inventory invoices"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email (Optional)"
                    type="email"
                    {...register('email', {
                      validate: value =>
                        !value ||
                        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ||
                        'Invalid email address',
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message || 'Single optional email for inventory profiles'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Physical Address
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Street Address" {...register('physicalAddress.street')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="City" {...register('physicalAddress.city')} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="State" {...register('physicalAddress.state')} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="ZIP Code" {...register('physicalAddress.zipCode')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Country" {...register('physicalAddress.country')} defaultValue="US" />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Invoice Instructions"
                    multiline
                    rows={4}
                    {...register('invoiceInstructions')}
                    placeholder="Enter sales invoice instructions for this bank..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Overnight Address
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Street Address" {...register('overnightAddress.street')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="City" {...register('overnightAddress.city')} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="State" {...register('overnightAddress.state')} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="ZIP Code" {...register('overnightAddress.zipCode')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Country" {...register('overnightAddress.country')} defaultValue="US" />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Contacts</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => append({ name: '', phone: '', email: '' })}
                      size="small"
                      variant="outlined"
                    >
                      Add Contact
                    </Button>
                  </Box>
                </Grid>
                {fields.map((field, index) => (
                  <React.Fragment key={field.id}>
                    <Grid item xs={12}>
                      <Paper elevation={1} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2">Contact {index + 1}</Typography>
                          {fields.length > 1 && (
                            <IconButton size="small" onClick={() => remove(index)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Name" {...register(`bankContacts.${index}.name` as const)} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Phone"
                              {...register(`bankContacts.${index}.phone` as const)}
                              placeholder="(555) 123-4567"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Email"
                              type="email"
                              {...register(`bankContacts.${index}.email` as const, {
                                validate: value =>
                                  !value ||
                                  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ||
                                  'Invalid email address',
                              })}
                              error={!!errors.bankContacts?.[index]?.email}
                              helperText={errors.bankContacts?.[index]?.email?.message}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </React.Fragment>
                ))}
              </>
            )}

            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onFormSubmit)}
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Profile'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

